import { SearchImage } from "@/assets/icons/24/SearchImage";
import { formatPrice } from "@/common/utils/intl.utils";
import { getPriceInfo } from "@/common/utils/product.utils";
import ProductUtils from "@/components/ProductDetail/utils/ProductUtils";
import { DesignerItemLabel } from "@/features/designer-item/components/DesignerItemLabel";
import { Flavor } from "@/ui/typography/Flavor";
import { Helper } from "@/ui/typography/Helper";
import { SEARCH_BY_IMAGE_COOKIE_NAME } from "@/features/iris/static/iris.static";
import React, { useCallback } from "react";
import { useTranslation } from "next-i18next";
import { cn } from "@/common/utils/css.utils";
import { useRouter } from "next/router";
import Link from "@whoppah/next/link";
import { ProductCardFavoriteButton } from "@/components/product/ProductCardFavoriteButton";
import { useCookies } from "react-cookie";
import ProductCardImage from "./ProductCardImage";
import { analyticsService } from "@/common/services/analytics/analytics.service";
import { SIMILAR_ROUTE } from "@/common/static/routes.static";

/**
 * @typedef {import("../../Apollo/schema").Product} Product
 * @typedef {import("type-fest").PartialDeep} PartialDeep
 *
 * @typedef {PartialDeep<Product>} ProductPartial
 */

/**
 * @typedef {object} ProductCardProps
 * @property {ProductPartial} [item]
 * @property {boolean} [preloadImage=false]
 * @property {string} [listName]
 * @property {object} [searchMeta]
 * @property {string} [searchMeta.queryId]
 * @property {string} [searchMeta.indexName]
 * @property {number} [searchMeta.page]
 * @property {number} [searchMeta.limit]
 * @property {number} [index]
 * @property {string} [className]
 * @property {() => void} [onClick]
 * @property {boolean} [showBrowseSimilar=false]
 */

/**
 * Product card.
 *
 * @type {React.FC<ProductCardProps>}
 * @return {React.ReactElement}
 */
const ProductCard = ({
  item,
  preloadFirstImage = false,
  listName = undefined,
  searchMeta = {},
  index = undefined,
  className = undefined,
  onClick = undefined,
  showBrowseSimilar = false,
}) => {
  const { t } = useTranslation();

  const { query } = useRouter();
  const [cookies] = useCookies([SEARCH_BY_IMAGE_COOKIE_NAME]);

  const searchImage = cookies.searchByImage ? query.image : undefined;

  /**
   * Path of the product page
   */
  const path = `/products/${item.slug}`;

  /**
   * Callback for tracking the item selection.
   */
  const handleClick = useCallback(() => {
    analyticsService.trackSelectItem(
      index + 1,
      listName,
      listName,
      item,
      searchMeta,
      searchImage
    );

    onClick?.();
  }, [index, listName, item, searchMeta, searchImage, onClick]);

  const allowBid = ProductUtils.allowBid(item);
  const isSold = ProductUtils.isSold(item);

  const {
    discounted,
    discountPercentage,
    price,
    soldAtPrice,
    originalPrice,
    bidPrice,
  } = getPriceInfo(item);

  /**
   * Callback for rendering the bid price of an item.
   */
  const renderBidPrice = useCallback(() => {
    if (!bidPrice || isSold) {
      return null;
    }

    return (
      <Helper className="bid-price text-grey-500">
        {t("common:product-card-bid-from", {
          price: formatPrice(bidPrice.amount),
        })}
      </Helper>
    );
  }, [bidPrice, isSold, t]);

  /**
   * Callback for rendering the price for which the item was sold.
   * @param soldPrice - The param for which the product was sold for.
   */
  const renderPriceLabel = useCallback(
    (price, strikethrough) => {
      if (!price) {
        return null;
      }

      return (
        <span
          className={cn(
            strikethrough ? "text-grey-500 line-through" : "font-bold"
          )}
        >
          {t(isSold ? "common:product-card-sold-for" : "common:common-price", {
            price: isSold ? formatPrice(soldAtPrice.amount) : price,
          })}
        </span>
      );
    },
    [isSold, soldAtPrice?.amount, t]
  );

  /**
   * Callback for rendering the price of the product.
   * Will render different prices based on the product's tate (if it has been sold, allows bidding, has discount etc...)
   */
  const renderPrice = useCallback(() => {
    if (isSold) {
      return renderPriceLabel(soldAtPrice);
    }

    if (discounted) {
      return (
        <span className="flex space-x-2">
          {renderPriceLabel(originalPrice, true)}
          {discounted && renderPriceLabel(price)}
        </span>
      );
    }

    return renderPriceLabel(price);
  }, [isSold, discounted, renderPriceLabel, price, soldAtPrice, originalPrice]);

  /**
   * Callback for rendering the product label.
   */
  const renderLabels = useCallback(() => {
    if (isSold) {
      return (
        <Helper className="absolute right-2 top-2 bg-brand-500 px-2 py-1 text-white">
          {t("common:auction-state-already-sold")}
        </Helper>
      );
    }

    if (discountPercentage) {
      return (
        <span className="absolute right-2 top-2 bg-attention-500 px-2 py-1">
          <Helper className="text-white">-{discountPercentage}%</Helper>
        </span>
      );
    }

    return null;
  }, [isSold, discountPercentage, t]);

  const renderBrowseSimilarItems = useCallback(() => {
    return (
      <Link href={`${SIMILAR_ROUTE}/${item.slug}`} passHref>
        <a
          href="#similar"
          className="absolute bottom-0 right-0 hidden items-center space-x-2 rounded-[0.375rem] bg-white px-4 py-2 group-hover:flex"
          onClick={handleBrowseSimilarClick}
        >
          <Flavor size="medium">
            {t("similar-products:similar-products-title")}
          </Flavor>
          <SearchImage className="size-6" />
        </a>
      </Link>
    );
  }, [item.slug, t]);

  /**
   *  Callback for browsing similar products.
   */
  const handleBrowseSimilarClick = evt => {
    /** Prevent navigation to product details. */
    evt.stopPropagation();

    /**
     * Triggers `show_similar_item_plp` event.
     */
    analyticsService.trackShowSimilarItemPlp();
  };

  return (
    <Link sourceLocale="en" href={path} prefetch={false} passHref>
      <a href="#pdp" onClick={handleClick}>
        <div
          className={cn(
            "group flex cursor-pointer flex-col space-y-4",
            className
          )}
        >
          <div className="relative rounded-[0.25rem] border border-grey-300 p-2 group-hover:shadow-[0_4px_20px_0px_rgba(0,0,0,0.20)]">
            <div className="aspect-h-1 aspect-w-1 relative">
              {item.images[0] ? (
                <ProductCardImage
                  preloadImage={preloadFirstImage}
                  className="transition-opacity md:group-hover:opacity-0"
                  image={item.images[0]}
                  alt={item.title}
                  isSold={isSold}
                />
              ) : (
                <div className="bg-grey-100" />
              )}
              {item.images[1] ? (
                <ProductCardImage
                  className={cn(
                    "opacity-0 transition-opacity ",
                    isSold
                      ? "md:group-hover:opacity-50"
                      : "md:group-hover:opacity-100"
                  )}
                  image={item.images[1]}
                  alt={item.title}
                  isSold={isSold}
                />
              ) : null}
            </div>
            {renderLabels()}
            {showBrowseSimilar && renderBrowseSimilarItems()}
          </div>
          <div className="flex flex-col space-y-2">
            {/* Title, Favorite */}
            <div className="flex space-x-2">
              <span className="line-clamp-2 flex-1 font-title text-base md:text-base">
                {item.title}
              </span>
              <ProductCardFavoriteButton item={item} />
            </div>
            {/* Price, Bid */}
            <div className="flex flex-col">
              <span className="font-title text-base md:text-lg">
                {renderPrice()}
              </span>
              {allowBid && (
                <span className="font-title text-sm md:text-base">
                  {renderBidPrice()}
                </span>
              )}
            </div>
            {/* Labels, Promo */}
            {item?.isDesignerItem && (
              <div className="flex flex-wrap space-x-2">
                <DesignerItemLabel product={item} />
              </div>
            )}
          </div>
        </div>
      </a>
    </Link>
  );
};

export default ProductCard;
